import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import useGraphStore from '@/store/graphStore';
import { useLanguage } from '@/context/LanguageContext';

const GraphFilters = ({ className, onClose }) => {
  const { language } = useLanguage();
  const { filters, setFilters, resetFilters, filteredNodes, filteredEdges, nodes, edges } = useGraphStore();
  const [searchValue, setSearchValue] = useState(filters.search);
  
  const t = {
    filters: language === 'de' ? 'Filter' : 'Filters',
    search: language === 'de' ? 'Adresse suchen...' : 'Search address...',
    amount: language === 'de' ? 'Betrag (BTC)' : 'Amount (BTC)',
    timeRange: language === 'de' ? 'Zeitraum' : 'Time Range',
    txType: language === 'de' ? 'Transaktionstyp' : 'Transaction Type',
    chain: language === 'de' ? 'Blockchain' : 'Blockchain',
    risk: language === 'de' ? 'Risikostufe' : 'Risk Level',
    connections: language === 'de' ? 'Min. Verbindungen' : 'Min Connections',
    reset: language === 'de' ? 'Zurücksetzen' : 'Reset',
    inflow: language === 'de' ? 'Eingang' : 'Inflow',
    outflow: language === 'de' ? 'Ausgang' : 'Outflow',
    low: language === 'de' ? 'Niedrig' : 'Low',
    medium: language === 'de' ? 'Mittel' : 'Medium',
    high: language === 'de' ? 'Hoch' : 'High',
    showing: language === 'de' ? 'Zeige' : 'Showing',
    of: language === 'de' ? 'von' : 'of',
    nodesLabel: language === 'de' ? 'Knoten' : 'nodes',
    edgesLabel: language === 'de' ? 'Kanten' : 'edges',
  };
  
  // Debounced search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setFilters({ search: searchValue });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, setFilters]);
  
  const handleCheckboxChange = (category, value, checked) => {
    const current = filters[category];
    const updated = checked
      ? [...current, value]
      : current.filter(v => v !== value);
    setFilters({ [category]: updated });
  };
  
  const timeRanges = [
    { value: '24h', label: '24h' },
    { value: '7d', label: '7d' },
    { value: '30d', label: '30d' },
    { value: 'all', label: language === 'de' ? 'Alle' : 'All' },
  ];

  return (
    <div className={cn("h-full flex flex-col bg-card", className)}>
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <i className="fa-solid fa-filter text-sm text-muted-foreground"></i>
          {t.filters}
        </h3>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            <i className="fa-solid fa-rotate-left mr-1"></i>
            {t.reset}
          </Button>
          {onClose && (
            <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
              <i className="fa-solid fa-xmark"></i>
            </Button>
          )}
        </div>
      </div>
      
      {/* Stats */}
      <div className="px-4 py-2 bg-muted/30 border-b border-border">
        <p className="text-xs text-muted-foreground">
          {t.showing} <span className="font-medium text-foreground">{filteredNodes.length}</span> {t.of} {nodes.length} {t.nodesLabel}, {' '}
          <span className="font-medium text-foreground">{filteredEdges.length}</span> {t.of} {edges.length} {t.edgesLabel}
        </p>
      </div>
      
      {/* Filters */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Search */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">{t.search}</Label>
          <div className="relative">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"></i>
            <Input
              type="text"
              placeholder={t.search}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
        
        {/* Amount Range */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">{t.amount}</Label>
            <span className="text-xs text-muted-foreground font-mono">
              {filters.amountRange[0].toFixed(2)} - {filters.amountRange[1].toFixed(2)}
            </span>
          </div>
          <Slider
            value={filters.amountRange}
            min={0}
            max={50}
            step={0.1}
            onValueChange={(value) => setFilters({ amountRange: value })}
            className="mt-2"
          />
        </div>
        
        {/* Time Range */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">{t.timeRange}</Label>
          <div className="flex flex-wrap gap-1">
            {timeRanges.map((range) => (
              <Button
                key={range.value}
                variant={filters.timeRange === range.value ? 'default' : 'outline'}
                size="sm"
                className="h-7 text-xs"
                onClick={() => setFilters({ timeRange: range.value })}
              >
                {range.label}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Transaction Type */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">{t.txType}</Label>
          <div className="space-y-2">
            {[
              { value: 'inflow', label: t.inflow, color: 'bg-success' },
              { value: 'outflow', label: t.outflow, color: 'bg-destructive' },
            ].map((type) => (
              <div key={type.value} className="flex items-center gap-2">
                <Checkbox
                  id={`tx-${type.value}`}
                  checked={filters.txTypes.includes(type.value)}
                  onCheckedChange={(checked) => handleCheckboxChange('txTypes', type.value, checked)}
                />
                <Label htmlFor={`tx-${type.value}`} className="text-sm flex items-center gap-2 cursor-pointer">
                  <span className={cn("w-2 h-2 rounded-full", type.color)}></span>
                  {type.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
        
        {/* Chain */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">{t.chain}</Label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'BTC', icon: 'fa-brands fa-bitcoin', color: '#f7931a' },
              { id: 'ETH', icon: 'fa-brands fa-ethereum', color: '#627eea' },
              { id: 'SOL', icon: 'fa-solid fa-sun', color: '#00ffa3' },
              { id: 'MATIC', icon: 'fa-solid fa-hexagon-vertical-nft', color: '#8247e5' },
              { id: 'AVAX', icon: 'fa-solid fa-mountain', color: '#e84142' },
              { id: 'BNB', icon: 'fa-solid fa-coins', color: '#f3ba2f' },
            ].map((chain) => (
              <div key={chain.id} className="flex items-center gap-2">
                <Checkbox
                  id={`chain-${chain.id}`}
                  checked={filters.chains.includes(chain.id)}
                  onCheckedChange={(checked) => handleCheckboxChange('chains', chain.id, checked)}
                />
                <Label htmlFor={`chain-${chain.id}`} className="text-sm flex items-center gap-2 cursor-pointer">
                  <i className={cn("text-sm", chain.icon)} style={{ color: chain.color }}></i>
                  {chain.id}
                </Label>
              </div>
            ))}
          </div>
        </div>
        
        {/* Risk Level */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">{t.risk}</Label>
          <div className="space-y-2">
            {[
              { value: 'low', label: t.low, color: 'bg-success' },
              { value: 'medium', label: t.medium, color: 'bg-warning' },
              { value: 'high', label: t.high, color: 'bg-destructive' },
            ].map((risk) => (
              <div key={risk.value} className="flex items-center gap-2">
                <Checkbox
                  id={`risk-${risk.value}`}
                  checked={filters.riskLevels.includes(risk.value)}
                  onCheckedChange={(checked) => handleCheckboxChange('riskLevels', risk.value, checked)}
                />
                <Label htmlFor={`risk-${risk.value}`} className="text-sm flex items-center gap-2 cursor-pointer">
                  <span className={cn("w-2 h-2 rounded-full", risk.color)}></span>
                  {risk.label}
                </Label>
              </div>
            ))}
          </div>
        </div>
        
        {/* Min Connections */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium">{t.connections}</Label>
            <span className="text-xs text-muted-foreground font-mono">{filters.minDegree}+</span>
          </div>
          <Slider
            value={[filters.minDegree]}
            min={0}
            max={20}
            step={1}
            onValueChange={([value]) => setFilters({ minDegree: value })}
          />
        </div>
      </div>
    </div>
  );
};

export default GraphFilters;
